import pdfMake from 'pdfmake/build/pdfmake';
import { vfs as pdfFontsVfs } from 'pdfmake/build/vfs_fonts';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { Worksheet, TimeEntry } from '../types/worksheet';

(pdfMake as any).vfs = pdfFontsVfs;

type TFunc = (key: string) => string;

interface GeneratePdfParams {
  worksheet: Worksheet;
  timeEntries: TimeEntry[];
  customerName: string;
  t: TFunc;
  /** fotók dataURL-ként (a hívó tölti le és tömöríti – lásd loadWorksheetPhotosForPdf) */
  photos?: { dataUrl: string; label: string; caption?: string }[];
}

/** A munkalap fotóit letölti és PDF-be ágyazható dataURL-lé alakítja (a nem elérhetőket kihagyja). */
export const loadWorksheetPhotosForPdf = async (worksheet: Worksheet): Promise<{ dataUrl: string; label: string; caption?: string }[]> => {
  const list = (worksheet.photos || []).slice().sort((a, b) => (a.phase === b.phase ? 0 : a.phase === 'before' ? -1 : b.phase === 'before' ? 1 : a.phase === 'after' ? -1 : 1));
  if (!list.length) return [];
  const { urlToDataUrl } = await import('./imageCompress');
  const out: { dataUrl: string; label: string; caption?: string }[] = [];
  for (const p of list) {
    const d = await urlToDataUrl(p.url, 900);
    if (d) out.push({ dataUrl: d, label: p.phase === 'before' ? 'Előtte' : p.phase === 'after' ? 'Utána' : 'Fotó', caption: p.caption });
  }
  return out;
};

const formatDate = (date: any): string => {
  if (!date) return '';
  try {
    if (date && typeof date.toDate === 'function') {
      return format(date.toDate(), 'yyyy. MM. dd.', { locale: hu });
    }
    if (date instanceof Date && !isNaN(date.getTime())) {
      return format(date, 'yyyy. MM. dd.', { locale: hu });
    }
    if (typeof date === 'string') {
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        return format(parsed, 'yyyy. MM. dd.', { locale: hu });
      }
    }
    return '';
  } catch {
    return '';
  }
};

const formatDateTime = (date: any): string => {
  if (!date) return '';
  try {
    if (date && typeof date.toDate === 'function') {
      return format(date.toDate(), 'yyyy. MM. dd. HH:mm:ss', { locale: hu });
    }
    if (date instanceof Date && !isNaN(date.getTime())) {
      return format(date, 'yyyy. MM. dd. HH:mm:ss', { locale: hu });
    }
    if (typeof date === 'string') {
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        return format(parsed, 'yyyy. MM. dd. HH:mm:ss', { locale: hu });
      }
    }
    return '';
  } catch {
    return '';
  }
};

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) return `${hours}ó ${mins}p`;
  return `${mins}p`;
};

const COLORS = {
  primary: '#2563eb',
  dark: '#1e293b',
  gray: '#64748b',
  lightGray: '#e2e8f0',
  veryLightGray: '#f8fafc',
  white: '#ffffff',
  green: '#059669',
  red: '#dc2626',
  amber: '#d97706',
  purple: '#7c3aed',
  cyan: '#0891b2',
};

const getStatusColor = (status: string): string => {
  const map: Record<string, string> = {
    draft: COLORS.gray,
    assigned: COLORS.primary,
    in_progress: COLORS.amber,
    review_pending: COLORS.purple,
    completed: COLORS.green,
    sent: COLORS.cyan,
    partially_invoiced: '#ea580c',
    invoiced: COLORS.green,
    cancelled: COLORS.red,
    hibernated: '#475569',
  };
  return map[status] || COLORS.gray;
};

export const generateWorksheetPdf = ({ worksheet, timeEntries, customerName, t, photos }: GeneratePdfParams) => {
  const docDefinition: any = {
    pageSize: 'A4',
    pageMargins: [40, 80, 40, 60],
    header: (currentPage: number, pageCount: number) => ({
      margin: [40, 30, 40, 0],
      stack: [
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: t('worksheets.print.title'), fontSize: 20, bold: true, color: COLORS.dark },
                { text: worksheet.worksheetNumber || '', fontSize: 11, color: COLORS.gray, margin: [0, 2, 0, 0] },
              ],
            },
            {
              width: 'auto',
              stack: [
                {
                  text: t(`worksheets.status.${worksheet.status}`),
                  fontSize: 10,
                  bold: true,
                  color: COLORS.white,
                  background: getStatusColor(worksheet.status),
                  padding: [4, 8],
                  alignment: 'center',
                },
              ],
            },
          ],
        },
        { canvas: [{ type: 'line', x1: 0, y1: 10, x2: 515, y2: 10, lineWidth: 2, lineColor: COLORS.primary }] },
      ],
    }),
    footer: (currentPage: number, pageCount: number) => ({
      margin: [40, 0, 40, 20],
      stack: [
        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: COLORS.lightGray }] },
        {
          columns: [
            { text: `${t('worksheets.print.generatedAt')} ${format(new Date(), 'yyyy. MM. dd. HH:mm:ss', { locale: hu })}`, fontSize: 8, color: COLORS.gray },
            { text: `${t('worksheets.print.subtitle')}  `, fontSize: 8, color: COLORS.gray, alignment: 'right' },
            { text: t('worksheets.print.domain'), fontSize: 8, color: COLORS.gray, alignment: 'right' },
          ],
          margin: [0, 4, 0, 0],
        },
        { text: `${currentPage} / ${pageCount}`, fontSize: 8, color: COLORS.gray, alignment: 'center', margin: [0, 2, 0, 0] },
      ],
    }),
    content: [] as any[],
    styles: {
      sectionHeader: { fontSize: 12, bold: true, color: COLORS.dark, margin: [0, 10, 0, 6] },
      infoLabel: { fontSize: 8, color: COLORS.gray, margin: [0, 0, 0, 2] },
      infoValue: { fontSize: 10, color: COLORS.dark, bold: true },
      tableHeader: { fontSize: 8, bold: true, color: COLORS.gray, fillColor: COLORS.veryLightGray },
      tableCell: { fontSize: 9, color: COLORS.dark },
      totalRow: { fontSize: 9, bold: true, color: COLORS.dark, fillColor: COLORS.veryLightGray },
      noteText: { fontSize: 9, color: COLORS.dark },
      noteMeta: { fontSize: 8, color: COLORS.gray },
      descriptionBox: { fontSize: 9, color: COLORS.dark, margin: [0, 4, 0, 0] },
    },
    defaultStyle: {
      fontSize: 10,
      color: COLORS.dark,
    },
  };

  const content = docDefinition.content;

  // === Basic Info ===
  content.push({ text: t('worksheets.print.basicInfo'), style: 'sectionHeader' });

  const basicInfoBody: any[] = [];
  basicInfoBody.push([
    { text: t('worksheets.fields.title'), style: 'infoLabel' },
    { text: worksheet.title || '-', style: 'infoValue' },
    { text: t('worksheets.fields.customer'), style: 'infoLabel' },
    { text: customerName, style: 'infoValue' },
  ]);
  basicInfoBody.push([
    { text: t('worksheets.fields.worksheetDate'), style: 'infoLabel' },
    { text: formatDate(worksheet.worksheetDate) || '-', style: 'infoValue' },
    { text: t('worksheets.fields.dueDate'), style: 'infoLabel' },
    { text: formatDate(worksheet.dueDate) || '-', style: 'infoValue' },
  ]);
  basicInfoBody.push([
    { text: t('worksheets.fields.workType'), style: 'infoLabel' },
    { text: t(`worksheets.workTypes.${worksheet.workType}`), style: 'infoValue' },
    { text: t('worksheets.fields.priority'), style: 'infoLabel' },
    { text: t(`worksheets.priority.${worksheet.priority}`), style: 'infoValue' },
  ]);
  basicInfoBody.push([
    { text: t('worksheets.fields.status'), style: 'infoLabel' },
    { text: t(`worksheets.status.${worksheet.status}`), style: 'infoValue', color: getStatusColor(worksheet.status) },
    { text: t('worksheets.fields.location'), style: 'infoLabel' },
    { text: worksheet.location || '-', style: 'infoValue' },
  ]);

  content.push({
    table: {
      widths: ['auto', '*', 'auto', '*'],
      body: basicInfoBody,
    },
    layout: {
      paddingLeft: () => 6,
      paddingRight: () => 6,
      paddingTop: () => 4,
      paddingBottom: () => 4,
      hLineColor: () => COLORS.lightGray,
      vLineColor: () => COLORS.lightGray,
    },
  });

  // === Work Details ===
  if (worksheet.description || worksheet.startTime || worksheet.endTime) {
    content.push({ text: t('worksheets.fields.workDetails'), style: 'sectionHeader' });

    if (worksheet.startTime && worksheet.endTime) {
      content.push({
        columns: [
          { width: 'auto', text: `${t('worksheets.print.workTime')}: `, style: 'infoLabel' },
          { width: '*', text: `${worksheet.startTime} - ${worksheet.endTime}`, style: 'infoValue' },
        ],
        margin: [0, 0, 0, 4],
      });
    }
    if (worksheet.description) {
      content.push({
        text: worksheet.description,
        style: 'descriptionBox',
      });
    }
  }

  // === Travel Info ===
  if (worksheet.travelKmStart || worksheet.travelKmEnd || worksheet.travelKmTotal || worksheet.travelCosts) {
    content.push({ text: t('worksheets.fields.travelInfo'), style: 'sectionHeader' });

    const travelBody: any[] = [[
      { text: t('worksheets.fields.travelKmStart'), style: 'tableHeader' },
      { text: t('worksheets.fields.travelKmEnd'), style: 'tableHeader' },
      { text: t('worksheets.fields.travelKmTotal'), style: 'tableHeader' },
      { text: t('worksheets.fields.travelCosts'), style: 'tableHeader' },
    ]];
    travelBody.push([
      { text: worksheet.travelKmStart ? `${worksheet.travelKmStart} km` : '-', style: 'tableCell', alignment: 'center' },
      { text: worksheet.travelKmEnd ? `${worksheet.travelKmEnd} km` : '-', style: 'tableCell', alignment: 'center' },
      { text: worksheet.travelKmTotal ? `${worksheet.travelKmTotal} km` : '-', style: 'tableCell', alignment: 'center' },
      { text: worksheet.travelCosts ? `${worksheet.travelCosts} Ft` : '-', style: 'tableCell', alignment: 'center' },
    ]);

    content.push({
      table: {
        widths: ['*', '*', '*', '*'],
        body: travelBody,
      },
      layout: {
        hFillColor: (i: number) => i === 0 ? COLORS.veryLightGray : null,
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 4,
        paddingBottom: () => 4,
        hLineColor: () => COLORS.lightGray,
        vLineColor: () => COLORS.lightGray,
      },
    });
  }

  // === Deployment Info ===
  if (worksheet.deploymentOccurred) {
    content.push({ text: t('worksheets.fields.deploymentInfo'), style: 'sectionHeader' });

    const deployBody: any[] = [[
      { text: t('worksheets.fields.deployment'), style: 'tableHeader' },
      { text: t('worksheets.fields.deploymentDate'), style: 'tableHeader' },
      { text: t('worksheets.fields.deploymentTime'), style: 'tableHeader' },
    ]];
    deployBody.push([
      { text: t('worksheets.fields.deploymentYes'), style: 'tableCell', alignment: 'center', color: COLORS.green, bold: true },
      { text: formatDate(worksheet.deploymentDate) || '-', style: 'tableCell', alignment: 'center' },
      { text: worksheet.deploymentTime || '-', style: 'tableCell', alignment: 'center' },
    ]);

    content.push({
      table: {
        widths: ['*', '*', '*'],
        body: deployBody,
      },
      layout: {
        hFillColor: (i: number) => i === 0 ? COLORS.veryLightGray : null,
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 4,
        paddingBottom: () => 4,
        hLineColor: () => COLORS.lightGray,
        vLineColor: () => COLORS.lightGray,
      },
    });
  }

  // === Materials & Equipment ===
  if (worksheet.materials || worksheet.equipment) {
    content.push({ text: t('worksheets.fields.materialsAndEquipment'), style: 'sectionHeader' });
    if (worksheet.materials) {
      content.push({
        text: `${t('worksheets.fields.materials')}: ${worksheet.materials}`,
        style: 'descriptionBox',
      });
    }
    if (worksheet.equipment) {
      content.push({
        text: `${t('worksheets.fields.equipment')}: ${worksheet.equipment}`,
        style: 'descriptionBox',
      });
    }
  }

  // === Items ===
  if (worksheet.items && worksheet.items.length > 0) {
    content.push({ text: t('worksheets.items.title'), style: 'sectionHeader' });

    const itemsBody: any[] = [[
      { text: t('worksheets.items.name'), style: 'tableHeader' },
      { text: t('worksheets.items.quantity'), style: 'tableHeader', alignment: 'center' },
      { text: t('worksheets.items.unit'), style: 'tableHeader', alignment: 'center' },
      { text: t('worksheets.items.unitPrice'), style: 'tableHeader', alignment: 'right' },
      { text: t('worksheets.items.vat'), style: 'tableHeader', alignment: 'center' },
      { text: t('worksheets.items.netTotal'), style: 'tableHeader', alignment: 'right' },
      { text: t('worksheets.items.grossTotal'), style: 'tableHeader', alignment: 'right' },
    ]];

    let totalNet = 0;
    let totalGross = 0;

    worksheet.items.forEach((item) => {
      if (item.type === 'header') {
        itemsBody.push([
          { text: item.name || '', style: 'tableCell', bold: true, colSpan: 7, fillColor: COLORS.veryLightGray },
          '', '', '', '', '', '',
        ]);
      } else {
        totalNet += item.totalNet || 0;
        totalGross += item.totalGross || 0;
        const itemText: any = { text: item.name || '', style: 'tableCell' };
        if (item.notes) {
          itemText.stack = [
            { text: item.name || '', style: 'tableCell' },
            { text: item.notes, fontSize: 7, color: COLORS.gray, margin: [0, 1, 0, 0] },
          ];
          delete itemText.text;
        }
        itemsBody.push([
          itemText,
          { text: String(item.quantity || 0), style: 'tableCell', alignment: 'center' },
          { text: item.unit || '', style: 'tableCell', alignment: 'center' },
          { text: `${(item.unitPrice || 0).toLocaleString('hu-HU')} Ft`, style: 'tableCell', alignment: 'right' },
          { text: `${item.vatRate || 0}%`, style: 'tableCell', alignment: 'center' },
          { text: `${(item.totalNet || 0).toLocaleString('hu-HU')} Ft`, style: 'tableCell', alignment: 'right' },
          { text: `${(item.totalGross || 0).toLocaleString('hu-HU')} Ft`, style: 'tableCell', alignment: 'right' },
        ]);
      }
    });

    itemsBody.push([
      { text: t('worksheets.items.total'), style: 'totalRow', colSpan: 5, alignment: 'right' },
      '', '', '', '',
      { text: `${totalNet.toLocaleString('hu-HU')} Ft`, style: 'totalRow', alignment: 'right' },
      { text: `${totalGross.toLocaleString('hu-HU')} Ft`, style: 'totalRow', alignment: 'right' },
    ]);

    content.push({
      table: {
        widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
        body: itemsBody,
      },
      layout: {
        hFillColor: (i: number, node: any) => {
          if (i === 0) return COLORS.veryLightGray;
          if (i === node.table.body.length - 1) return COLORS.veryLightGray;
          return null;
        },
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 3,
        paddingBottom: () => 3,
        hLineColor: () => COLORS.lightGray,
        vLineColor: () => COLORS.lightGray,
      },
    });
  }

  // === Tasks ===
  if (worksheet.tasks && worksheet.tasks.length > 0) {
    const completedCount = worksheet.tasks.filter((task: any) => task.isCompleted || task.completed).length;
    content.push({
      text: `${t('worksheets.tasks.title')} (${completedCount}/${worksheet.tasks.length})`,
      style: 'sectionHeader',
    });

    const taskLines: any[] = worksheet.tasks.map((task: any) => {
      const isDone = task.isCompleted || task.completed;
      return {
        columns: [
          { width: 'auto', text: isDone ? '☑' : '☐', fontSize: 11, color: isDone ? COLORS.green : COLORS.gray, margin: [0, 0, 6, 0] },
          {
            width: '*',
            text: task.title + (task.description ? ` — ${task.description}` : '') + (task.estimatedMinutes ? ` (${task.estimatedMinutes} p)` : ''),
            style: 'tableCell',
            decoration: isDone ? 'lineThrough' : '',
            color: isDone ? COLORS.gray : COLORS.dark,
          },
        ],
        margin: [0, 2, 0, 2],
      };
    });

    content.push(...taskLines);
  }

  // === Time Entries ===
  if (timeEntries && timeEntries.length > 0) {
    content.push({ text: t('worksheets.timeTracking.title'), style: 'sectionHeader' });

    const timeBody: any[] = [[
      { text: t('worksheets.timeTracking.startLabel'), style: 'tableHeader' },
      { text: t('worksheets.timeTracking.endLabel'), style: 'tableHeader' },
      { text: t('worksheets.timeTracking.duration'), style: 'tableHeader', alignment: 'center' },
      { text: t('worksheets.timeTracking.workersCount'), style: 'tableHeader', alignment: 'center' },
      { text: t('worksheets.timeTracking.type'), style: 'tableHeader', alignment: 'center' },
      { text: t('worksheets.timeTracking.description'), style: 'tableHeader' },
    ]];

    let totalMinutes = 0;

    timeEntries.forEach((entry: any) => {
      const start = entry.startTime?.toDate ? entry.startTime.toDate() : new Date(entry.startTime);
      const end = entry.endTime?.toDate ? entry.endTime.toDate() : (entry.endTime ? new Date(entry.endTime) : null);
      totalMinutes += entry.duration || 0;

      timeBody.push([
        { text: start ? format(start, 'yyyy. MM. dd. HH:mm') : '-', style: 'tableCell' },
        { text: end ? format(end, 'yyyy. MM. dd. HH:mm') : '-', style: 'tableCell' },
        { text: formatDuration(entry.duration || 0), style: 'tableCell', alignment: 'center' },
        { text: String(entry.workerCount || 1), style: 'tableCell', alignment: 'center' },
        { text: entry.isManual ? t('worksheets.timeTracking.manualType') : t('worksheets.timeTracking.timerType'), style: 'tableCell', alignment: 'center' },
        { text: entry.description || '', style: 'tableCell' },
      ]);
    });

    timeBody.push([
      { text: t('worksheets.items.total'), style: 'totalRow', colSpan: 2, alignment: 'right' },
      '',
      { text: formatDuration(totalMinutes), style: 'totalRow', alignment: 'center' },
      { text: '', style: 'totalRow' },
      { text: '', style: 'totalRow' },
      { text: '', style: 'totalRow' },
    ]);

    content.push({
      table: {
        widths: ['auto', 'auto', 'auto', 'auto', 'auto', '*'],
        body: timeBody,
      },
      layout: {
        hFillColor: (i: number, node: any) => {
          if (i === 0) return COLORS.veryLightGray;
          if (i === node.table.body.length - 1) return COLORS.veryLightGray;
          return null;
        },
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 3,
        paddingBottom: () => 3,
        hLineColor: () => COLORS.lightGray,
        vLineColor: () => COLORS.lightGray,
      },
    });
  }

  // === Notes ===
  if (worksheet.notes_list && worksheet.notes_list.length > 0) {
    content.push({ text: t('worksheets.notes.title'), style: 'sectionHeader' });

    worksheet.notes_list.forEach((note: any) => {
      const visibilityLabel = note.visibility === 'customer' ? t('worksheets.notes.customer') : t('worksheets.notes.internal');
      const createdDate = note.createdAt?.toDate ? format(note.createdAt.toDate(), 'yyyy. MM. dd. HH:mm') : '';
      content.push({
        stack: [
          {
            text: `[${visibilityLabel}] ${note.text}`,
            style: 'noteText',
          },
          {
            text: `${note.createdByName || ''} — ${createdDate}`,
            style: 'noteMeta',
            margin: [0, 2, 0, 0],
          },
        ],
        margin: [0, 0, 0, 6],
      });
    });
  }

  // === Safety Notes ===
  if (worksheet.safetyNotes) {
    content.push({ text: t('worksheets.fields.safetyNotes'), style: 'sectionHeader' });
    content.push({ text: worksheet.safetyNotes, style: 'descriptionBox' });
  }

  // === Special Notes ===
  if (worksheet.specialNotes) {
    content.push({ text: t('worksheets.fields.specialNotes'), style: 'sectionHeader' });
    content.push({ text: worksheet.specialNotes, style: 'descriptionBox' });
  }

  // === Signatures ===
  if (worksheet.signatures && worksheet.signatures.length > 0) {
    content.push({ text: t('worksheets.fields.signatures'), style: 'sectionHeader' });

    worksheet.signatures.forEach((signature) => {
      content.push({
        stack: [
          {
            columns: [
              { width: '*', text: signature.signerName, style: 'infoValue' },
              { width: 'auto', text: signature.signerRole, style: 'noteMeta' },
            ],
          },
          {
            image: signature.signatureData,
            width: 200,
            height: 60,
            margin: [0, 4, 0, 0],
          },
          {
            text: formatDateTime(signature.signedAt),
            style: 'noteMeta',
            margin: [0, 2, 0, 0],
          },
        ],
        margin: [0, 0, 0, 10],
      });
    });
  }

  // === Fotók (2 oszlop) ===
  if (photos && photos.length > 0) {
    content.push({ text: 'Fotódokumentáció', style: 'sectionHeader', pageBreak: 'before' });
    for (let i = 0; i < photos.length; i += 2) {
      const pair = photos.slice(i, i + 2);
      content.push({
        columns: pair.map(p => ({
          width: '*',
          stack: [
            { image: p.dataUrl, fit: [240, 180], alignment: 'center' },
            { text: [p.label, p.caption ? ` – ${p.caption}` : ''].join(''), style: 'noteMeta', alignment: 'center', margin: [0, 3, 0, 0] }
          ],
          margin: [0, 0, 8, 10]
        })),
        columnGap: 8
      });
    }
  }

  // Generate and download
  const fileName = `${t('worksheets.print.title')}_${worksheet.worksheetNumber || worksheet.id}.pdf`;
  (pdfMake as any).createPdf(docDefinition).download(fileName);
};
